import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  tmsApi,
  type CargoDefinition,
  type CreateCargoDefinitionRequest,
  type UpdateCargoDefinitionRequest,
} from '../client';

/**
 * Stable query-key factory for cargo-definition-scoped caches. Same shape
 * as `serviceTagKeys` so a future suite-wide tweak (e.g. a global stale
 * time) can iterate keys with the same prefix.
 */
export const cargoDefinitionKeys = {
  all: ['tms', 'cargo-definition'] as const,
  byOrganisation: (organisationId: string) =>
    ['tms', 'cargo-definition', 'org', organisationId] as const,
};

/**
 * Fetch every cargo definition for an organisation. Pass `null` to keep the
 * query disabled (e.g. while the org id is still resolving from Auth0).
 *
 * The list response includes hydrated `equipmentCatalogueKeys` and
 * `serviceTagIds` for each definition; the FE resolves equipment labels via
 * the cached catalogue (`@my/directory-api`) and tag names via the same-org
 * service-tag list (`useServiceTags`).
 */
export function useCargoDefinitions(
  organisationId: string | null,
  options?: Omit<UseQueryOptions<CargoDefinition[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<CargoDefinition[], Error>({
    queryKey: organisationId
      ? cargoDefinitionKeys.byOrganisation(organisationId)
      : cargoDefinitionKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return tmsApi.getCargoDefinitions(organisationId);
    },
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Mutation wrapper around POST `/tms/api/v1/cargoDefinitions`. Invalidates
 * the per-org list cache on success so the new row shows up.
 */
export function useCreateCargoDefinition() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateCargoDefinitionRequest>({
    mutationKey: [...cargoDefinitionKeys.all, 'create'],
    mutationFn: (body) => tmsApi.createCargoDefinition(body),
    onSuccess: (_id, vars) => {
      qc.invalidateQueries({
        queryKey: cargoDefinitionKeys.byOrganisation(vars.organisationId),
      });
    },
  });
}

/**
 * Mutation wrapper around PUT `/tms/api/v1/cargoDefinitions`. The caller
 * passes `organisationId` alongside the body so we can target the per-org
 * list cache without an extra round-trip — same pattern as
 * `useUpdateServiceTag`.
 */
export function useUpdateCargoDefinition() {
  const qc = useQueryClient();
  return useMutation<string, Error, { organisationId: string; body: UpdateCargoDefinitionRequest }>({
    mutationKey: [...cargoDefinitionKeys.all, 'update'],
    mutationFn: ({ body }) => tmsApi.updateCargoDefinition(body),
    onSuccess: (_id, { organisationId }) => {
      qc.invalidateQueries({
        queryKey: cargoDefinitionKeys.byOrganisation(organisationId),
      });
    },
  });
}

/**
 * Mutation wrapper around DELETE `/tms/api/v1/cargoDefinitions/{id}`. Soft
 * delete on the BE — the row stays in the DB with `IsDeleted=true` but
 * disappears from the per-org list via the global query filter.
 */
export function useDeleteCargoDefinition() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { organisationId: string; id: string }>({
    mutationKey: [...cargoDefinitionKeys.all, 'delete'],
    mutationFn: ({ id }) => tmsApi.deleteCargoDefinition(id),
    onSuccess: (_res, { organisationId }) => {
      qc.invalidateQueries({
        queryKey: cargoDefinitionKeys.byOrganisation(organisationId),
      });
    },
  });
}
